import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";

const LOADING_STATES = ["loading", "generating"];

// Consumes the SWR envelope { status, data } and polls while data is being fetched
// in the background, so responses are always fast (no request ever blocks on MCP).
export function useNovexData(path, { live = false, liveMs = 30000, pollMs = 2500, maxPolls = 15, enabled = true } = {}) {
  const [state, setState] = useState({ status: "loading", data: null, source: null, error: null });
  const polls = useRef(0);
  const timer = useRef(null);

  const fetchOnce = useCallback(async (silent) => {
    try {
      const { data: env } = await api.get(path);
      const st = env?.status || "ok";
      if (LOADING_STATES.includes(st)) {
        polls.current += 1;
        if (polls.current <= maxPolls) {
          if (!silent) setState((s) => ({ ...s, status: "loading" }));
          timer.current = setTimeout(() => fetchOnce(silent), pollMs);
        } else {
          setState({ status: "timeout", data: null, error: "Données Novex indisponibles pour le moment." });
        }
        return;
      }
      polls.current = 0;
      if (st === "limit") {
        setState({ status: "limit", data: null, source: null, error: "Limite quotidienne Novex atteinte (reset à 00:00 UTC)." });
      } else if (st === "error") {
        setState({ status: "error", data: null, source: null, error: env.detail || "Indisponible" });
      } else {
        setState({ status: "ok", data: env.data !== undefined ? env.data : env, source: env.source || null, error: null });
      }
    } catch (e) {
      polls.current = 0;
      setState({ status: "error", data: null, source: null, error: e?.response?.status === 429 ? "Limite quotidienne Novex atteinte." : "Erreur de chargement" });
    }
  }, [path, pollMs, maxPolls]);

  const reload = useCallback(() => {
    polls.current = 0;
    clearTimeout(timer.current);
    setState((s) => ({ ...s, status: "loading" }));
    fetchOnce(false);
  }, [fetchOnce]);

  useEffect(() => {
    if (!enabled) return undefined;
    reload();
    let liveTimer;
    if (live) liveTimer = setInterval(() => fetchOnce(true), liveMs);
    return () => {
      clearTimeout(timer.current);
      if (liveTimer) clearInterval(liveTimer);
    };
  }, [enabled, live, liveMs, reload, fetchOnce]);

  return { ...state, loading: state.status === "loading", reload };
}
